import { useCallback, useEffect } from "react";
import {
    ICustomScrollSelector,
    ICustomScrollStore,
} from "../_store/CustomScrollStoreModel";
import { IDimension } from "../scroll/perfectScrollModel";
import { setOffsetsOnScrollAction } from "../_features/setOffsetsOnScroll/setOffsetsOnScrollActions";
import { IThumbParams } from "../_shared/IThumbParams";
import { Action1, Func } from "../_utils/fnTypes";
import { useStateEx } from "../_utils/hooks";
import { callAllFns, noopFn } from "../_utils/fnUtils";
import { bindEvent, bindEventOnce } from "../_utils/domElementEventHelpers";

const defaultArgs = {
    isDragging: false,
    isTouchMode: false,
    startingScrollTop: null,
    startingMousePageY: null,
    scrollBy: null,
};
type IArgs = typeof defaultArgs;

const buildDragThumbHandler = (
    pageMeasureKey: string,
    selector: ICustomScrollSelector<IDimension>
) => (
    element: HTMLElement,
    getC: Func<IThumbParams>,
    { getState, dispatch }: ICustomScrollStore
) => {
    const [i, setI, getI] = useStateEx(defaultArgs);

    useEffect(
        () =>
            i.isDragging
                ? bindMoves(
                      getI,
                      setI,
                      dispatch,
                      pageMeasureKey,
                      element?.ownerDocument || document
                  )
                : noopFn,
        [i.isDragging]
    );

    return {
        isDragging: i.isDragging,

        onMouseDown: useCallback(
            startMoves.bind(
                null,
                selector,
                getState,
                getC,
                setI,
                pageMeasureKey,
                false
            ),
            []
        ),

        onTouchStart: useCallback(
            startMoves.bind(
                null,
                selector,
                getState,
                getC,
                setI,
                pageMeasureKey,
                true
            ),
            []
        ),
    };
};

function startMoves(
    selector,
    getState,
    getC: Func<IThumbParams>,
    setI,
    pageMeasureKey,
    isTouchMode,
    e
) {
    const { contentMeasure, containerMeasure, scrollMeasure } = selector(
        getState()
    );
    const { thumbMeasure, railMeasure } = getC();

    setI({
        isDragging: true,
        isTouchMode,
        startingMousePageY: e.touches
            ? e.touches[0][pageMeasureKey]
            : e[pageMeasureKey],
        startingScrollTop: scrollMeasure,
        scrollBy:
            (contentMeasure - containerMeasure) / (railMeasure - thumbMeasure),
    });

    if (!isTouchMode) e.preventDefault();

    e.stopPropagation();
}

function bindMoves(
    getI: Func<IArgs>,
    setI: Action1<IArgs>,
    dispatch,
    pageMeasureKey,
    ownerDocument: EventTarget
) {
    const onMoveFn = onMove.bind(null, getI, dispatch, pageMeasureKey);

    const [moveKey, stopKey]: any = getI().isTouchMode
        ? ["touchmove", "touchend"]
        : ["mousemove", "mouseup"];

    return callAllFns(
        bindEvent(ownerDocument, moveKey, onMoveFn),
        bindEventOnce(ownerDocument, stopKey, () => {
            setI(defaultArgs);
        })
    );
}

function onMove(getI, dispatch, pageMeasureKey, e) {
    const eventMeasure =
        e.touches && e.touches[0]
            ? e.touches[0][pageMeasureKey]
            : e[pageMeasureKey];

    const {
        startingScrollTop,
        startingMousePageY,
        scrollBy,
        isDragging,
    } = getI();

    const args = [
        undefined,
        startingScrollTop + scrollBy * (eventMeasure - startingMousePageY),
    ];

    if (pageMeasureKey === "pageY") args.reverse();
    dispatch(setOffsetsOnScrollAction.apply(null, args));

    e.stopPropagation();
    if (e.type.startsWith("touch") && e.changedTouches.length > 1) {
        e.preventDefault();
    }
}

/*
 */
export const useDragVerticalThumbHandler = buildDragThumbHandler(
    "pageY",
    s => s.verticalDimension
);

/*
 */
export const useDragHorizontalThumbHandler = buildDragThumbHandler(
    "pageX",
    s => s.horizontalDimension
);
